/**
 * Session state for the dashboard.
 *
 * The access token lives in memory inside `@minirag/api` — deliberately not in
 * localStorage, where any XSS could read it. Continuity across reloads comes
 * from the httpOnly refresh cookie: on mount we call /auth/refresh once, and if
 * the cookie is still valid the user is back where they were.
 */
import { api, errorMessage, onUnauthorized, setAccessToken } from '@minirag/api';
import type { Schema } from '@minirag/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type User = Schema<'UserRead'>;
type Tenant = Schema<'TenantRead'>;

export interface Session {
  user: User;
  /** Null for an OWNER/ADMIN when the system has no active workspace at all
   * yet — a fresh install, or every workspace deactivated. Everyone else
   * always has one. */
  tenant: Tenant | null;
  /** Every tenant this user may switch to — every active tenant in the
   * system for OWNER/ADMIN, just their one pinned tenant otherwise. Length 1
   * means there's nothing to switch between, whether or not the role could
   * in principle switch. */
  tenants: Tenant[];
  permissions: string[];
}

type LoginShaped = Pick<
  Schema<'LoginResponse'>,
  'access_token' | 'user' | 'tenant' | 'tenants' | 'permissions'
>;

interface AuthContextValue {
  session: Session | null;
  /** True until the initial refresh attempt settles — render nothing routed yet. */
  isBootstrapping: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Permission check driven by the server's list, never a local role table. */
  can: (permission: string) => boolean;
  /** Apply a login-shaped response obtained elsewhere — e.g. accept-invite,
   * which signs the new user in as part of a different endpoint. */
  adoptSession: (response: LoginShaped) => void;
  /** Move the session to a different tenant. Clears every cached query —
   * cached bot/source/chat data belongs to the tenant just left and would
   * otherwise render as if it belonged to the new one. */
  switchTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isBootstrapping, setBootstrapping] = useState(true);
  const queryClient = useQueryClient();

  const clear = useCallback(() => {
    setAccessToken(null);
    setSession(null);
  }, []);

  /** Load the current user once an access token is in place. */
  const loadSession = useCallback(async (): Promise<boolean> => {
    const { data, error } = await api.GET('/v1/auth/me');
    if (error || !data) return false;
    setSession({
      user: data.user,
      tenant: data.tenant,
      tenants: data.tenants,
      permissions: data.permissions,
    });
    return true;
  }, []);

  // Restore a session from the refresh cookie on first load.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data, error } = await api.POST('/v1/auth/refresh');
      if (!cancelled && !error && data?.access_token) {
        setAccessToken(data.access_token);
        await loadSession();
      }
      if (!cancelled) setBootstrapping(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // The API client calls this when a refresh fails — the session is gone.
  useEffect(() => {
    onUnauthorized(clear);
  }, [clear]);

  const adoptSession = useCallback((response: LoginShaped) => {
    setAccessToken(response.access_token);
    setSession({
      user: response.user,
      tenant: response.tenant,
      tenants: response.tenants,
      permissions: response.permissions,
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await api.POST('/v1/auth/login', {
        body: { email, password },
      });
      if (error || !data) {
        throw new Error(errorMessage(error, 'Could not sign in'));
      }
      adoptSession(data);
    },
    [adoptSession],
  );

  const logout = useCallback(async () => {
    // Revoke server-side first; clear locally regardless of the outcome so a
    // network failure cannot strand the user in a half-signed-in state.
    await api.POST('/v1/auth/logout').catch(() => undefined);
    clear();
  }, [clear]);

  const can = useCallback(
    (permission: string) => session?.permissions.includes(permission) ?? false,
    [session],
  );

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const { data, error } = await api.POST('/v1/auth/switch-tenant', {
        body: { tenant_id: tenantId },
      });
      if (error || !data) {
        throw new Error(errorMessage(error, 'Could not switch workspace'));
      }
      setAccessToken(data.access_token);
      // Every cached list/detail belongs to the tenant just left.
      queryClient.clear();
      const ok = await loadSession();
      if (!ok) throw new Error('Switched, but could not reload the session');
    },
    [loadSession, queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ session, isBootstrapping, login, logout, can, adoptSession, switchTenant }),
    [session, isBootstrapping, login, logout, can, adoptSession, switchTenant],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
